import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, CalendarDays, Clock, Globe2, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Reveal, Field, inputCls } from "@/components/ui-bits";

export default function ExpertBook() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ctype, setCtype] = useState("expert");
  const [date, setDate] = useState("");
  const [dates, setDates] = useState(null);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(60);
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState(null);
  const [refForm, setRefForm] = useState({ reference: "", proof: null, doc: null });
  const [docBefore, setDocBefore] = useState(null);
  const [paySending, setPaySending] = useState(false);
  const [payDone, setPayDone] = useState(false);
  const [docDone, setDocDone] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);

  useEffect(() => {
    api.get(`/experts/${id}/booking-info`).then((r) => {
      setInfo(r.data);
      setCtype(r.data.supports_expert ? "expert" : "research");
      const durs = r.data.availability?.durations?.length ? r.data.availability.durations : [60];
      setDuration(durs[0]);
    }).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    setDates(null);
    setDate("");
    setSlots(null);
    setTime("");
    api.get(`/experts/${id}/available-dates`, { params: { duration } })
      .then((r) => setDates(r.data.dates))
      .catch(() => setDates([]));
  }, [id, duration]);

  useEffect(() => {
    if (!date) return;
    setSlots(null);
    setTime("");
    api.get(`/experts/${id}/slots`, { params: { date, duration } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [date, id, duration]);

  const rate = ctype === "research" ? (info?.research_hourly_rate ?? 50) : (info?.hourly_rate ?? 50);
  const total = useMemo(() => Math.round((rate * duration) / 60 * 100) / 100, [rate, duration]);
  const avail = info?.availability || { days: [], slots: [], schedule: [] };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(
    lang === "ar" ? "ar" : "en-GB", { weekday: "short", day: "numeric", month: "short" });
  const schedulingMode = showScheduling || (dates !== null && dates.length === 0);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/expert-bookings", {
        expert_id: id,
        client_name: form.client_name || "", email: form.email || "",
        phone: form.phone || "", whatsapp: form.whatsapp || "",
        country: form.country || "",
        topic: form.topic || "", notes: form.notes || "",
        research_stage: form.research_stage || "", support_type: form.support_type || "",
        consultation_type: ctype,
        date, time, duration,
      });
      setBooking(data);
      if (docBefore) {
        try {
          const fd = new FormData();
          fd.append("document", docBefore);
          await api.post(`/expert-bookings/${data.id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          toast.error(t("form.error"));
        }
      }
      toast.success(t("book.success"));
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 409) {
        api.get(`/experts/${id}/slots`, { params: { date, duration } }).then((r) => { setSlots(r.data.slots); setTime(""); });
        api.get(`/experts/${id}/available-dates`, { params: { duration } }).then((r) => setDates(r.data.dates));
      }
    } finally {
      setSending(false);
    }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    setPaySending(true);
    try {
      if (refForm.reference || refForm.proof) {
        const fd = new FormData();
        fd.append("reference", refForm.reference);
        if (refForm.proof) fd.append("proof", refForm.proof);
        await api.post(`/expert-bookings/${booking.id}/payment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPayDone(true);
      }
      if (refForm.doc) {
        const fd2 = new FormData();
        fd2.append("document", refForm.doc);
        await api.post(`/expert-bookings/${booking.id}/document`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
        setDocDone(true);
      }
      if (!refForm.reference && !refForm.proof && !refForm.doc) {
        toast.error(t("form.error"));
        return;
      }
      toast.success(t("reg.payDone"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPaySending(false);
    }
  };

  if (notFound)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-500" data-testid="booking-not-found">{t("notFound.title")}</div>;
  if (!info)
    return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-400">{t("common.loading")}</div>;

  const wiseText = booking ? (booking[lang === "ar" ? "wise_instructions_ar" : "wise_instructions_en"] || booking.wise_instructions_en) : "";

  return (
    <div data-testid="expert-booking-page">
      <section className="bg-navy-950 py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal>
            <Link to={`/experts/${id}`} data-testid="booking-back-link" className="text-xs font-bold uppercase tracking-wider text-gold hover:text-gold-light">← {info.full_name}</Link>
            <h1 className="mt-4 font-display text-4xl font-semibold text-white sm:text-5xl">{t("book.title")}</h1>
            <p className="mt-3 text-slate-300">{info.full_name} — {info.job_title}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          {booking ? (
            <Reveal className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
              <div data-testid="booking-confirmation">
                <CheckCircle2 className="h-12 w-12 text-gold" />
                <h2 className="mt-4 font-display text-3xl font-semibold text-navy">{t("book.success")}</h2>
                <div className="mt-5 grid gap-2 rounded-lg bg-slate-50 p-5 text-sm sm:grid-cols-2">
                  <p><span className="font-semibold text-slate-500">{t("book.id")}:</span> <span className="font-mono font-bold text-navy" data-testid="booking-id-value">{booking.booking_id}</span></p>
                  <p><span className="font-semibold text-slate-500">{t("book.type")}:</span> {t(booking.consultation_type === "research" ? "join.typeResearch" : "join.typeExpert")}</p>
                  <p><span className="font-semibold text-slate-500">{t("book.total")}:</span> <span className="font-bold text-navy">USD {Number(booking.total_amount).toLocaleString()}</span></p>
                  <p><span className="font-semibold text-slate-500">{t("book.selectTime")}:</span> {date} · {time} ({info.availability.timezone})</p>
                </div>
                <h3 className="mt-8 font-display text-xl font-semibold text-navy">{t("book.payTitle")}</h3>
                <p className="mt-3 whitespace-pre-line rounded-lg bg-gold-soft p-5 text-sm leading-relaxed text-slate-700" data-testid="booking-wise-instructions">{wiseText}</p>
                {payDone ? (
                  <p className="mt-5 rounded-lg bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid="booking-payment-submitted">{t("book.pendingNote")}</p>
                ) : (
                  <form onSubmit={submitPayment} className="mt-5 space-y-4" data-testid="booking-payment-form">
                    <Field label={t("reg.reference")}>
                      <input className={inputCls} value={refForm.reference} onChange={(e) => setRefForm({ ...refForm, reference: e.target.value })} data-testid="booking-reference-input" />
                    </Field>
                    <Field label={t("reg.proof")}>
                      <input type="file" accept=".pdf,image/*" className="block w-full text-sm text-slate-600 file:me-3 file:rounded-full file:border-0 file:bg-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-white"
                        onChange={(e) => setRefForm({ ...refForm, proof: e.target.files[0] || null })} data-testid="booking-proof-input" />
                    </Field>
                    {booking.consultation_type === "research" && (
                      <Field label={t("book.doc")}>
                        <input type="file" accept=".pdf,.doc,.docx,image/*" className="block w-full text-sm text-slate-600 file:me-3 file:rounded-full file:border-0 file:bg-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-white"
                          onChange={(e) => setRefForm({ ...refForm, doc: e.target.files[0] || null })} data-testid="booking-doc-input" />
                      </Field>
                    )}
                    <button type="submit" disabled={paySending} data-testid="booking-payment-submit"
                      className="rounded-full bg-navy px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-800 disabled:opacity-60">
                      {paySending ? t("form.sending") : t("reg.paySubmit")}
                    </button>
                  </form>
                )}
                {docDone && <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid="booking-doc-uploaded">✓</p>}
                <p className="mt-6 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">{t("book.pendingNote")}</p>
                <p className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">{t("book.portalNote")}</p>
              </div>
            </Reveal>
          ) : schedulingMode ? (
            <SchedulingRequestForm expertId={id} ctype={ctype} />
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="expert-booking-form">
              {info.supports_expert && info.supports_research && (
                <Reveal className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
                  <label className="field-label">{t("book.type")}</label>
                  <div className="flex flex-wrap gap-2" data-testid="booking-type-group">
                    <button type="button" onClick={() => setCtype("expert")} data-testid="booking-type-expert"
                      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${ctype === "expert" ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {t("join.typeExpert")}
                    </button>
                    <button type="button" onClick={() => setCtype("research")} data-testid="booking-type-research"
                      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${ctype === "research" ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {t("join.typeResearch")}
                    </button>
                  </div>
                </Reveal>
              )}

              <Reveal className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
                <h2 className="mb-5 font-display text-2xl font-semibold text-navy">{t("book.selectDate")} &amp; {t("book.selectTime")}</h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={t("book.selectDate")} required>
                    {dates === null ? (
                      <p className="text-sm text-slate-400">{t("common.loading")}</p>
                    ) : (
                      <div className="flex max-h-44 flex-wrap gap-2 overflow-y-auto pe-1" data-testid="booking-dates">
                        {dates.map((d) => (
                          <button type="button" key={d} onClick={() => setDate(d)} data-testid={`booking-date-${d}`}
                            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${date === d ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-gold-soft"}`}>
                            {fmtDate(d)}
                          </button>
                        ))}
                      </div>
                    )}
                  </Field>
                  <Field label={t("book.duration")}>
                    <div className="flex flex-wrap gap-2" data-testid="booking-duration-group">
                      {(avail.durations?.length ? avail.durations : [60]).map((d) => (
                        <button type="button" key={d} onClick={() => setDuration(d)} data-testid={`booking-duration-${d}`}
                          className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${duration === d ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                          {d} {t("book.minutes")}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
                {date && (
                  <div className="mt-5">
                    <label className="field-label">{t("book.selectTime")} <Globe2 className="inline h-3.5 w-3.5 text-gold" /> <span className="normal-case text-slate-400">({info.availability.timezone})</span></label>
                    {slots === null ? (
                      <p className="text-sm text-slate-400">{t("common.loading")}</p>
                    ) : slots.length === 0 ? (
                      <div className="rounded-lg bg-slate-50 p-4" data-testid="booking-no-slots">
                        <p className="text-sm text-slate-500">{t("book.noSlots")}</p>
                        <button type="button" onClick={() => setShowScheduling(true)} data-testid="booking-request-scheduling-link"
                          className="mt-2 text-sm font-bold text-gold-hover underline underline-offset-4 hover:text-navy">
                          {t("book.rsInstead")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2" data-testid="booking-slots">
                        {slots.map((s) => (
                          <button type="button" key={s} onClick={() => setTime(s)} data-testid={`booking-slot-${s.replace(":", "")}`}
                            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${time === s ? "bg-gold text-navy" : "bg-slate-100 text-slate-700 hover:bg-gold-soft"}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 rounded-lg bg-navy p-5 text-sm text-white">
                  <span className="inline-flex items-center gap-2"><Banknote className="h-4 w-4 text-gold" />{t("book.rate")}: <strong>USD {rate}/h</strong></span>
                  <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-gold" />{duration} {t("book.minutes")}</span>
                  <span className="inline-flex items-center gap-2 text-base"><CalendarDays className="h-4 w-4 text-gold" />{t("book.total")}: <strong className="text-gold" data-testid="booking-total">USD {total.toLocaleString()}</strong></span>
                </div>
              </Reveal>

              <Reveal className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
                <h2 className="mb-5 font-display text-2xl font-semibold text-navy">{t("book.details")}</h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={t("form.fullName")} required><input required className={inputCls} value={form.client_name || ""} onChange={set("client_name")} data-testid="booking-name" /></Field>
                  <Field label={t("form.email")} required><input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="booking-email" /></Field>
                  <Field label={t("form.phone")}><input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="booking-phone" /></Field>
                  <Field label={t("form.whatsapp")}><input className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="booking-whatsapp" /></Field>
                  <Field label={t("form.country")}><input className={inputCls} value={form.country || ""} onChange={set("country")} data-testid="booking-country" /></Field>
                  <Field label={ctype === "research" ? t("book.topic") : t("book.topic")} required><input required className={inputCls} value={form.topic || ""} onChange={set("topic")} data-testid="booking-topic" /></Field>
                  {ctype === "research" && (
                    <>
                      <Field label={t("book.stage")}><input className={inputCls} value={form.research_stage || ""} onChange={set("research_stage")} data-testid="booking-stage" /></Field>
                      <Field label={t("book.support")}><input className={inputCls} value={form.support_type || ""} onChange={set("support_type")} data-testid="booking-support" /></Field>
                      <div className="sm:col-span-2">
                        <Field label={t("book.doc")} hint={t("book.docHint")}>
                          <input type="file" accept=".pdf,.doc,.docx,image/*" className="block w-full text-sm text-slate-600 file:me-3 file:rounded-full file:border-0 file:bg-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-white"
                            onChange={(e) => setDocBefore(e.target.files[0] || null)} data-testid="booking-doc-before-input" />
                        </Field>
                      </div>
                    </>
                  )}
                  <div className="sm:col-span-2">
                    <Field label={t("form.message")}><textarea rows={3} className={inputCls} value={form.notes || ""} onChange={set("notes")} data-testid="booking-notes" /></Field>
                  </div>
                </div>
                <button type="submit" disabled={sending || !date || !time} data-testid="booking-submit-button"
                  className="mt-6 rounded-full bg-gold px-10 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover disabled:opacity-60">
                  {sending ? t("form.sending") : t("book.submit")}
                </button>
              </Reveal>
              <FreeConsultationCta />
            </form>
          )}
        </div>
      </section>
    </div>
  );
}


function FreeConsultationCta() {
  const { t } = useLanguage();
  return (
    <Reveal className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-6 text-center sm:p-7">
      <p className="mx-auto max-w-2xl text-sm leading-relaxed text-slate-600" data-testid="free-consultation-note">
        {t("book.freeNote")}
      </p>
      <Link to="/consultation" data-testid="free-consultation-cta"
        className="mt-4 inline-block rounded-full border border-navy px-6 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">
        {t("book.freeCta")}
      </Link>
    </Reveal>
  );
}

function SchedulingRequestForm({ expertId, ctype }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/experts/${expertId}/scheduling-requests`, {
        client_name: form.client_name || "", email: form.email || "",
        phone: form.phone || "", whatsapp: form.whatsapp || "", country: form.country || "",
        consultation_type: ctype, topic: form.topic || "", preferred_times: form.preferred_times || "",
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <Reveal className="rounded-lg border border-slate-200 bg-white p-10 shadow-sm sm:p-12">
        <div data-testid="scheduling-request-success">
          <CheckCircle2 className="h-12 w-12 text-gold" />
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-700">{t("book.rsSuccess")}</p>
          <Link to="/consultation" data-testid="scheduling-success-free-cta"
            className="mt-6 inline-block rounded-full border border-navy px-6 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">
            {t("book.freeCta")}
          </Link>
        </div>
      </Reveal>
    );
  }

  return (
    <>
      <Reveal className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
        <h2 className="font-display text-2xl font-semibold text-navy">{t("book.rsTitle")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="scheduling-request-intro">{t("book.rsIntro")}</p>
        <form onSubmit={submit} className="mt-6 grid gap-5 sm:grid-cols-2" data-testid="scheduling-request-form">
          <Field label={t("form.fullName")} required><input required className={inputCls} value={form.client_name || ""} onChange={set("client_name")} data-testid="scheduling-name" /></Field>
          <Field label={t("form.email")} required><input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="scheduling-email" /></Field>
          <Field label={t("form.phone")}><input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="scheduling-phone" /></Field>
          <Field label={t("form.whatsapp")}><input className={inputCls} value={form.whatsapp || ""} onChange={set("whatsapp")} data-testid="scheduling-whatsapp" /></Field>
          <Field label={t("form.country")}><input className={inputCls} value={form.country || ""} onChange={set("country")} data-testid="scheduling-country" /></Field>
          <Field label={t("book.topic")}><input className={inputCls} value={form.topic || ""} onChange={set("topic")} data-testid="scheduling-topic" /></Field>
          <div className="sm:col-span-2">
            <Field label={t("book.rsPreferred")}><textarea rows={3} className={inputCls} value={form.preferred_times || ""} onChange={set("preferred_times")} data-testid="scheduling-preferred" /></Field>
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={sending} data-testid="scheduling-request-submit"
              className="rounded-full bg-gold px-10 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover disabled:opacity-60">
              {sending ? t("form.sending") : t("book.rsSubmit")}
            </button>
          </div>
        </form>
      </Reveal>
      <FreeConsultationCta />
    </>
  );
}
